/**
 * API Configuration
 * Centralized configuration for backend API endpoints
 */

/**
 * Production: use origin only (e.g. https://zoniq.in) OR API root (https://zoniq.in/api).
 * If this already ends with `/api`, getApiUrl will not add a second `/api` prefix.
 */
export const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_BASE_URL || 'http://zoniq.in') 
  : '';

// API endpoints mapped to backend OpenAPI specification
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    SEND_OTP: '/auth/email-otp/send',
    VERIFY_OTP: '/auth/email-otp/verify',
    SIGNIN: '/auth/signin',
    SIGNUP: '/auth/signup',
    LOGOUT: '/auth/logout',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
  },
  // Accounts (Personal Profile Management)
  ACCOUNTS: {
    LIST: '/accounts',
    DETAIL: (id: string) => `/accounts/${id}`,
  },
  // Events
  EVENTS: {
    LIST: '/events',
    SEARCH: '/events/search',
    UPCOMING: '/events/upcoming',
    CATEGORIES: '/events/categories',
    DETAIL: (idOrSlug: string) => `/events/${idOrSlug}`,
    EVENT_CATEGORIES: (id: string) => `/events/${id}/categories`,
    SEATS: (id: string) => `/events/${id}/seats`,
    OCCURRENCES: (id: string) => `/events/${id}/occurrences`,
  },
  OCCURRENCES: {
    DETAIL: (id: string) => `/occurrences/${id}`,
  },
  // Activities (buyer site)
  ACTIVITIES: {
    CATALOG: (eventId: string | number) => `/events/${eventId}/activities`,
    VALIDATE_SELECTION: '/activities/validate-selection',
  },
  // Promo codes
  PROMO: {
    VALIDATE: '/promo/validate',
  },
  // Contact / partnership enquiry form
  CONTACT: {
    ENQUIRY: '/contact',
  },
  // Cart
  CART: {
    BASE: '/cart',
    ITEMS: '/cart/items',
    ITEM_DETAIL: (itemId: string) => `/cart/items/${itemId}`,
    VALIDATE: '/cart/validate',
    CHECKOUT: '/cart/checkout',
    CURRENT: '/cart/current',
    CLEAR: '/cart/clear',
    COUNT: '/cart/count',
  },
  // User & Bookings
  USER: {
    ACCOUNT: '/users/me',
    PROFILE: '/users/me/profile',
    PROFILE_PICTURE: '/users/me/profile/picture',
    VERIFY_PHONE: '/users/me/verify-phone',
    VERIFY_PHONE_CONFIRM: '/users/me/verify-phone/confirm',
    VERIFY_EMAIL: '/users/me/verify-email',
    VERIFY_EMAIL_CONFIRM: '/users/me/verify-email/confirm',
    BOOKINGS: '/users/me/bookings',
    WISHLIST: '/users/me/wishlist',
    WISHLIST_EVENT: (eventId: string) => `/users/me/wishlist/${eventId}`,
    WISHLIST_CHECK: (eventId: string) => `/users/me/wishlist/${eventId}/check`,
    NOTIFICATIONS: '/users/me/notifications/preferences',
  },
  // Bookings
  BOOKINGS: {
    DETAIL: (id: string) => `/bookings/${id}`,
    BY_NUMBER: (num: string) => `/bookings/number/${num}`,
    CANCEL: (id: string) => `/bookings/${id}/cancel`,
  },
  // Payments
  PAYMENTS: {
    CREATE_ORDER: '/payments/create-order',
    VERIFY: '/payments/verify',
    DETAIL: (id: string) => `/payments/${id}`,
    BOOKING_STATUS: (bookingId: string) => `/payments/booking/${bookingId}/status`,
    BOOKING_CANCEL: (bookingId: string) => `/payments/booking/${bookingId}/cancel`,
    WEBHOOK: '/payments/webhook',
  },
  // Tickets
  TICKETS: {
    DETAIL: (id: string) => `/tickets/${id}`,
    DOWNLOAD: (id: string) => `/tickets/${id}/download`,
    MARK_USED: '/tickets/mark-used',
    TRANSFER: (id: string) => `/tickets/${id}/transfer`,
    CLAIM: '/tickets/claim',
    CANCEL_TRANSFER: (transferId: string) => `/tickets/transfers/${transferId}/cancel`,
    TRANSFER_HISTORY: '/tickets/transfers/history',
    BY_BOOKING: (bookingId: string) => `/tickets/booking/${bookingId}`,
  },
  // Queue
  QUEUE: {
    JOIN: (eventId: string) => `/queue/${eventId}/join`,
    POSITION: (eventId: string) => `/queue/${eventId}/position`,
    LEAVE: (eventId: string) => `/queue/${eventId}/leave`,
    STATUS: (eventId: string) => `/queue/${eventId}/status`,
  },
  // AQI
  AQI: {
    SCORE: '/aqi/score',
    SCORE_FROM_USER: '/aqi/score-from-user',
  },
  ANNOUNCEMENTS: '/announcements',
  // Admin Routes
  ADMIN: {
    ANNOUNCEMENTS: {
      BASE: '/admin/announcements',
      DETAIL: (id: string | number) => `/admin/announcements/${id}`,
    },
    PROMO_CODES: {
      BASE: '/admin/promo-codes',
      DETAIL: (id: string | number) => `/admin/promo-codes/${id}`,
    },
    USERS: {
      BASE: '/admin/users',
      STATS: '/admin/users/stats',
      DETAIL: (id: string) => `/admin/users/${id}`,
    },
    LOGS: {
      BASE: '/admin/logs',
      DETAIL: (id: string) => `/admin/logs/${id}`,
    },
    VENUES: {
      BASE: '/admin/venues',
      ALL: '/admin/venues/all',
      DETAIL: (id: string) => `/admin/venues/${id}`,
    },
    EVENTS: {
      BASE: '/admin/events',
      STATS: '/admin/events/stats',
      DETAIL: (id: string) => `/admin/events/${id}`,
      PUBLISH: (id: string) => `/admin/events/${id}/publish`,
      CANCEL: (id: string) => `/admin/events/${id}/cancel`,
      REACTIVATE: (id: string) => `/admin/events/${id}/reactivate`,
      CATEGORIES: (id: string) => `/admin/events/${id}/categories`,
      CATEGORY_DETAIL: (id: string) => `/admin/events/categories/${id}`,
      OCCURRENCES: (id: string) => `/admin/events/${id}/occurrences`,
      OCCURRENCES_GENERATE: (id: string) => `/admin/events/${id}/occurrences/generate`,
      MEDIA_UPLOAD: (id: string) => `/admin/events/${id}/media/upload`,
      MEDIA: (id: string) => `/admin/events/${id}/media`,
      MEDIA_ITEM: (eventId: string, mediaId: string) => `/admin/events/${eventId}/media/${mediaId}`,
    },
    OCCURRENCES: {
      DETAIL: (id: string) => `/admin/occurrences/${id}`,
      TIERS: (id: string) => `/admin/occurrences/${id}/tiers`,
    },
    ACTIVITIES: {
      BY_EVENT: (eventId: string | number) => `/admin/events/${eventId}/activities`,
      BULK: (eventId: string | number) => `/admin/events/${eventId}/activities/bulk`,
      DETAIL: (id: number) => `/admin/activities/${id}`,
      POSTERS: (id: number) => `/admin/activities/${id}/posters`,
      WINDOWS: (id: number) => `/admin/activities/${id}/windows`,
      WINDOWS_COPY: (id: number) => `/admin/activities/${id}/windows/copy`,
      SLOTS: (id: number) => `/admin/activities/${id}/slots`,
      SLOT: (slotId: number) => `/admin/activity-slots/${slotId}`,
      GROUPS_BY_EVENT: (eventId: string | number) => `/admin/events/${eventId}/activity-groups`,
      GROUP: (id: number) => `/admin/activity-groups/${id}`,
    },
    SCHEDULE_PREVIEW: '/admin/schedule/preview',
    SEATS: {
      EVENT: (id: string) => `/admin/seats/event/${id}`,
      EVENT_COUNTS: (id: string) => `/admin/seats/event/${id}/counts`,
      EVENT_BULK: (id: string) => `/admin/seats/event/${id}/bulk`,
      DETAIL: (id: string) => `/admin/seats/${id}`,
      BLOCK: (id: string) => `/admin/seats/${id}/block`,
      UNBLOCK: (id: string) => `/admin/seats/${id}/unblock`,
      RELEASE: (id: string) => `/admin/seats/${id}/release`,
      RELEASE_EXPIRED: '/admin/seats/release-expired',
    },
    BOOKINGS: {
      BASE: '/admin/bookings',
      STATS: '/admin/bookings/stats',
      ANALYTICS: '/admin/bookings/analytics',
      TOP_EVENTS: '/admin/bookings/top-events',
      DETAIL: (id: string) => `/admin/bookings/${id}`,
      ATTENDEES: (eventId: string) => `/admin/bookings/event/${eventId}/attendees`,
    },
    DASHBOARD: {
      OVERVIEW: '/admin/dashboard/overview',
      REVENUE: '/admin/dashboard/revenue-chart',
      RECENT_BOOKINGS: '/admin/dashboard/recent-bookings',
      UPCOMING_EVENTS: '/admin/dashboard/upcoming-events',
    },
  }
};

/**
 * Build the full URL for a backend route.
 *
 * - Dev (empty base): returns `/api/...` for the CRA proxy.
 * - Prod base without `/api` (e.g. https://zoniq.in): returns `{base}/api/...`
 * - Prod base with `/api` (e.g. https://zoniq.in/api): returns `{base}/...` only - avoids `/api/api/...`
 */
export const getApiUrl = (endpoint: string): string => {
  let path = endpoint.trim();
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  const normalizedPath = path.startsWith('/api') ? path : `/api${path}`;

  if (API_BASE_URL === '') {
    return normalizedPath;
  }

  const baseUrl = API_BASE_URL.replace(/\/+$/, '');
  const baseEndsWithApi = /\/api$/i.test(baseUrl);

  if (baseEndsWithApi) {
    let suffix: string;
    if (normalizedPath.startsWith('/api')) {
      suffix = normalizedPath.slice(4);
      if (!suffix || suffix === '') {
        suffix = '/';
      }
    } else {
      suffix = normalizedPath;
    }
    if (!suffix.startsWith('/')) {
      suffix = `/${suffix}`;
    }
    return `${baseUrl}${suffix}`;
  }

  const tail = normalizedPath.startsWith('/') ? normalizedPath.slice(1) : normalizedPath;
  return `${baseUrl}/${tail}`;
};

/**
 * Helper function to get auth headers
 */
export const getAuthHeaders = (token?: string): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const authToken = token || localStorage.getItem('zoniq_token');
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  return headers;
};
